import React, { useState, useEffect, useRef } from 'react';

function fmt(d) { return d.toISOString().slice(0, 10); }
function todayStr() { return fmt(new Date()); }
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}
function endOfWeek(date) { return addDays(startOfWeek(date), 6); }
function startOfMonth(date) { const d = new Date(date); d.setDate(1); return d; }
function endOfMonth(date) {
  const d = new Date(date); d.setMonth(d.getMonth() + 1); d.setDate(0); return d;
}

const MIN_SINCE = '2025-09-01';

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'last_7', label: 'Last 7 days' },
  { key: 'last_30', label: 'Last 30 days' },
  { key: 'last_6m', label: 'Last 6 months' },
  { key: 'all', label: 'All time' },
];

export function computePreset(preset) {
  const today = new Date();
  switch (preset) {
    case 'today':       return { since: fmt(today), until: fmt(today), preset };
    case 'yesterday': { const y = addDays(today, -1); return { since: fmt(y), until: fmt(y), preset }; }
    case 'last_7':      return { since: fmt(addDays(today, -6)), until: fmt(today), preset };
    case 'last_30':     return { since: fmt(addDays(today, -29)), until: fmt(today), preset };
    case 'last_6m':   { const s6 = new Date(today); s6.setMonth(s6.getMonth() - 6); return { since: fmt(s6), until: fmt(today), preset }; }
    case 'all':         return { since: '2025-09-01', until: fmt(today), preset };
    case 'this_week':   return { since: fmt(startOfWeek(today)), until: fmt(endOfWeek(today)), preset };
    case 'last_week': { const t = addDays(today, -7); return { since: fmt(startOfWeek(t)), until: fmt(endOfWeek(t)), preset }; }
    case 'this_month':  return { since: fmt(startOfMonth(today)), until: fmt(endOfMonth(today)), preset };
    case 'last_month': { const t = addDays(startOfMonth(today), -1); return { since: fmt(startOfMonth(t)), until: fmt(endOfMonth(t)), preset }; }
    case 'q2_2026':     return { since: '2026-04-01', until: '2026-06-30', preset };
    default:            return null;
  }
}

export const defaultRange = computePreset('last_week');

export function formatLabel(value) {
  if (!value) return '—';
  const preset = value.preset;
  const named = PRESETS.find(p => p.key === preset);
  if (named) return named.label;
  // Custom or unknown preset → show date range
  const sinceD = new Date(value.since + 'T00:00:00Z');
  const untilD = new Date(value.until + 'T00:00:00Z');
  const opt = { month: 'short', day: 'numeric' };
  if (value.since === value.until) return sinceD.toLocaleDateString('en-US', opt);
  return sinceD.toLocaleDateString('en-US', opt) + ' — ' + untilD.toLocaleDateString('en-US', opt);
}

export default function DateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customSince, setCustomSince] = useState(value?.since || '');
  const [customUntil, setCustomUntil] = useState(value?.until || '');
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Keep custom inputs in sync when value changes externally
  useEffect(() => {
    if (value?.since) setCustomSince(value.since);
    if (value?.until) setCustomUntil(value.until);
  }, [value?.since, value?.until]);

  function pickPreset(key) {
    const r = computePreset(key);
    if (!r) return;
    if (onChange) onChange(r);
    setOpen(false);
  }

  function applyCustom() {
    if (!customSince || !customUntil) { alert('Please pick both since and until dates'); return; }
    if (customSince > customUntil) { alert('since must be ≤ until'); return; }
    if (customSince < MIN_SINCE) { alert('since must be ≥ ' + MIN_SINCE); return; }
    if (customUntil > todayStr()) { alert('until cannot be in the future'); return; }
    if (onChange) onChange({ since: customSince, until: customUntil, preset: 'custom' });
    setOpen(false);
  }

  const activeKey = value?.preset || null;

  return (
    <div className="perf-daterange" ref={popoverRef}>
      <button
        type="button"
        className={'perf-hctrl' + (open ? ' open' : '')}
        onClick={() => setOpen(v => !v)}
        title={value ? value.since + ' → ' + value.until : 'Pick a date range'}>
        📅 {formatLabel(value)}
        <span className="perf-hctrl-chev">▾</span>
      </button>
      {open && (
        <div className="perf-daterange-popover">
          <div className="perf-daterange-presets">
            {PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                className={'perf-daterange-preset' + (activeKey === p.key ? ' active' : '')}
                onClick={() => pickPreset(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="perf-daterange-custom">
            <div className="perf-daterange-custom-label">Custom range</div>
            <div className="perf-daterange-custom-inputs">
              <input
                type="date"
                min={MIN_SINCE}
                max={todayStr()}
                value={customSince}
                onChange={e => setCustomSince(e.target.value)} />
              <span className="perf-daterange-arrow">→</span>
              <input
                type="date"
                min={customSince || MIN_SINCE}
                max={todayStr()}
                value={customUntil}
                onChange={e => setCustomUntil(e.target.value)} />
            </div>
            <button
              type="button"
              className={'perf-daterange-apply' + (activeKey === 'custom' ? ' active' : '')}
              onClick={applyCustom}>
              Apply custom
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
